import { FC, useMemo } from 'react'

import { styled } from 'styled-components'

import CopyButton from 'core/components/lib/CopyButton'
import { onSurface, outline, surface } from 'core/styles'

type RawJsonProps = {
  className?: string
  /** The value to serialize. Passed through untouched. */
  value: unknown
  /** Visible rows before the field scrolls. */
  rows?: number
}

const serialize = (value: unknown): string => {
  try {
    return JSON.stringify(value, null, 2) ?? 'undefined'
  } catch {
    return 'Value could not be serialized'
  }
}

/**
 * The serialized response as plain text, for copying verbatim into Postman or
 * a query. Deliberately unstyled beyond a monospace font: no markup wraps the
 * characters, so a manual selection can pick up nothing extra.
 */
const RawJson: FC<RawJsonProps> = ({ className, value, rows = 16 }) => {
  const text = useMemo(() => serialize(value), [value])

  return (
    <Wrapper className={className}>
      <Header>
        <CopyButton label='Copy raw' text={text} />
      </Header>
      <Field aria-label='Raw JSON' readOnly rows={rows} value={text} />
    </Wrapper>
  )
}

export default RawJson

const Wrapper = styled.div`
  border: 1px solid ${(p) => p.theme.border ?? outline};
  border-radius: 6px;
  background-color: ${(p) => p.theme.offset ?? surface};
`

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  border-bottom: 1px solid ${(p) => p.theme.hairline ?? outline};
  padding: 4px 8px;
`

const Field = styled.textarea`
  display: block;
  border: none;
  background: none;
  padding: 8px;
  width: 100%;
  resize: vertical;
  line-height: 1.55;
  color: ${(p) => p.theme.text ?? onSurface};
  font-family: 'SF Mono', 'JetBrains Mono', Menlo, Consolas, monospace;
  font-size: 12px;

  &:focus {
    outline: none;
  }
`
